import clsx from 'clsx';
import { memo, useEffect, useMemo } from 'react';

import { useTopScorerStore } from '../store';
import { getPlayerIdByArray, getImageUrl, getKind, getStatByName } from '../utils/misc';

import BigCard from './BigCard';
import SmallCard from './SmallCard';

import { useWindowSize } from '@hooks/useWindowSize';

const MainGrid = ({ options }) => {
  const playersInfo = useTopScorerStore((state) => state.playersInfo ?? []);
  const editorialInfo = useTopScorerStore((state) => state.editorialInfo);
  const loadEditorialInfo = useTopScorerStore((state) => state.loadEditorialInfo);
  const { isDesktop } = useWindowSize();

  const getOddEven = (length) => {
    return length % 2 !== 0 || length > 4 ? 'odd' : 'even';
  };

  useEffect(() => {
    playersInfo && loadEditorialInfo(getPlayerIdByArray(playersInfo));
  }, [playersInfo, loadEditorialInfo]);

  const firstItem = playersInfo[0];
  const secondThirdItems = playersInfo.length > 2 ? playersInfo.slice(1, 3) : [];
  const topScorers = playersInfo.filter((o) => getStatByName(o, 'goals') === getStatByName(firstItem, 'goals'));
  const topScorerGrid = topScorers.length === 1 ? 'uniq' : getOddEven(topScorers.length);

  const secondarySlot = useMemo(() => {
    if (options?.enableEnhancedStatistics) {
      return playersInfo.length > 3 ? playersInfo.slice(3, playersInfo.length) : [];
    }
    return playersInfo.length > topScorers?.length ? playersInfo.slice(topScorers?.length, playersInfo.length) : [];
  }, [topScorers.length, options?.enableEnhancedStatistics, playersInfo]);

  const items = useMemo(() => {
    let val = getStatByName(firstItem, 'goals');
    let idCard = 0;
    return secondarySlot.map((item, index) => {
      if (getStatByName(item, 'goals') < val) {
        idCard = index + topScorers.length + 1;
      }
      val = getStatByName(item, 'goals');
      return {
        item,
        idCard,
        index,
      };
    });
  }, [firstItem, secondarySlot, topScorers.length]);

  return (
    <>
      <div className='pk-container ts-cardDisplayer--wide'>
        <div
          className={clsx(
            `pk-col pk-col--span-12 pk-col--span-lg-10 pk-col--offset-lg-1 ${
              !options?.enableEnhancedStatistics ? `ts-playeritems--${topScorerGrid}` : ''
            }`,
          )}
        >
          {options?.enableEnhancedStatistics && firstItem && (
            <BigCard
              idCard={1}
              size='big'
              player={firstItem}
              grid='uniq'
              options={options}
              kind={getKind(firstItem, editorialInfo)}
              imageUrl={getImageUrl(firstItem, editorialInfo)}
            />
          )}
          {!options?.enableEnhancedStatistics &&
            topScorers?.map((item) => (
              <BigCard
                key={item.player.id}
                idCard={1}
                size='big'
                grid={topScorerGrid}
                player={item}
                options={options}
                kind={getKind(item, editorialInfo)}
                imageUrl={getImageUrl(item, editorialInfo)}
              />
            ))}
        </div>

        {options?.enableEnhancedStatistics && (
          <div className='pk-col pk-col--span-12 pk-col--span-lg-10 pk-col--offset-lg-1 pk-mt-md--l'>
            <div className='ts-mosaic'>
              {secondThirdItems.map((item, index) => (
                <BigCard
                  key={item.player.id}
                  idCard={index + 2}
                  size='big'
                  grid=''
                  player={item}
                  options={options}
                  kind={getKind(item, editorialInfo)}
                  imageUrl={getImageUrl(item, editorialInfo)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <div className='pk-container ts-cardDisplayer--square pk-pt-md--48 pk-pt--0' pk-theme='light'>
        <div className='pk-col pk-col--span-12 pk-col--span-lg-10 pk-col--offset-lg-1 pk-mt-md--l'>
          <div className={clsx(isDesktop && 'ts-mosaic')}>
            {items.map(({ item, idCard, index }) => (
              <SmallCard
                idCard={options.enableEnhancedStatistics ? index + 4 : idCard}
                player={item}
                key={item.player.id}
                divider={!isDesktop && index !== items.length - 1}
                options={options}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default memo(MainGrid);
